import globalMessage from "@/utils/globalMessage";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Spin, Modal, Row, Col, Alert } from "antd";
import DetailTabs from "@components/DetailTabs";
import { api } from "@redux/api";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { useSelector } from "react-redux";
import moment from "moment";

import Facility from "@containers/management/Facility/Facility";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { Auth, AUTH_LEVEL } from "@/components/Auth";
import CompanySetting from "../CompanySetting";
import CompanyCostAccountSettings from "../CompanyCostAccountSettings";
import "./index.css";

const { TabPane } = DetailTabs;
const { TextArea } = Input;

const CompanyDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  
  // Determine if this is a create page from route
  const isCreatePage = isAdd || location.pathname === '/admin/create-company';
  
  const [loading, setLoading] = useState(isCreatePage ? false : true);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [companyData, setCompanyData] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1");

  const userName = useSelector(state => 
    state?.Auth?.user || 
    state?.Auth?.first_name + " " + state?.Auth?.last_name
  );
  const isCompanyAdmin = useSelector((state) => state.Auth?.is_companyadmin);
  const authRole = useSelector((state) => state.Auth?.role);

  useEffect(() => {
    if (!isCreatePage && id) {
      fetchCompanyDetail();
    }
  }, [id, isCreatePage]);

  const fetchCompanyDetail = async () => {
    try {
      setLoading(true);
      const response = await api.filterCompany(`companyId=${id}`);
      
      if (response.status === 200 && response.data.results.length > 0) {
        const data = response.data.results[0];
        setCompanyData(data);
        
        // Set form values
        const formValues = {
          ...data,
          created_date: data.created_date ? moment(data.created_date).format("YYYY-MM-DD HH:mm:ss") : "",
          modified_date: data.modified_date ? moment(data.modified_date).format("YYYY-MM-DD HH:mm:ss") : "",
        };
        form.setFieldsValue(formValues);
      } else {
        message.error("Company not found");
      }
    } catch (error) {
      console.error("Error fetching company:", error);
      message.error("Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setReadOnly(false);
  };

  const handleCancel = () => {
    setReadOnly(true);
    if (companyData) {
      const formValues = {
        ...companyData,
        created_date: companyData.created_date ? moment(companyData.created_date).format("YYYY-MM-DD HH:mm:ss") : "",
        modified_date: companyData.modified_date ? moment(companyData.modified_date).format("YYYY-MM-DD HH:mm:ss") : "",
      };
      form.setFieldsValue(formValues);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const submitValues = { ...values };
      
      let response;
      if (isCreatePage) {
        // Create mode
        response = await api.createCompany(submitValues);
      } else {
        // Edit mode
        submitValues.modifiedby_name = userName;
        response = await api.editCompany(id, submitValues);
      }
      
      if (response.status === 200 || response.status === 201) {
        globalMessage.success({
          message: "Success",
          description: isCreatePage ? "Company created successfully" : "Company updated successfully",
        });
        
        if (isCreatePage) {
          // Refresh Company list page and close current tab
          refreshTab("/admin/company");
          closeTab();
          navigate("/admin/company");
        } else {
          setReadOnly(true);
          fetchCompanyDetail();
        }
      } else {
        globalMessage.error({
          message: "Error",
          description: isCreatePage ? "Failed to create company" : "Failed to update company",
        });
      }
    } catch (error) {
      console.error(`Error ${isCreatePage ? 'creating' : 'updating'} company:`, error);
      globalMessage.error({
        message: "Error",
        description: isCreatePage ? "Failed to create company" : "Failed to update company",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this company?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteCompany(id);
          
          if (response.status === 204 || response.status === 200) {
            globalMessage.success({
              message: "Success",
              description: "Company deleted successfully",
            });
            // Refresh Company list page and close current tab
            refreshTab("/admin/company");
            closeTab();
            navigate("/admin/company");
          } else {
            globalMessage.error({
              message: "Error",
              description: "Failed to delete company",
            });
          }
        } catch (error) {
          console.error("Error deleting company:", error);
          globalMessage.error({
            message: "Error",
            description: "Failed to delete company",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCancelCreate = () => {
    closeTab();
    navigate("/admin/company");
  };

  const renderOperationButton = () => {
    if (isCreatePage) {
      // Create mode - only show Add button
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={handleCancelCreate}>
            <i className="fa-solid fa-xmark pr-2"></i>Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
        </div>
      );
    }
    
    if (readOnly) {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Auth code="company" level={AUTH_LEVEL.WRITE}>
            <Button type="primary" onClick={handleEdit}>
              <i className="fa-solid fa-pen-to-square pr-2"></i>Edit
            </Button>
          </Auth>
          <Auth code="company" level={AUTH_LEVEL.FULL_ACCESS}>
            <Button danger onClick={handleDelete}>
              <i className="fa-solid fa-trash pr-2"></i>Delete
            </Button>
          </Auth>
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2"></i>Save
          </Button>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      );
    }
  };


  // FacilityRelated function
  const FacilityRelated = (relatedId) => {
    return <>{relatedId ? <Facility relatedList={true} relatedId={relatedId} relatedObject={'facility'}/> : ''}</>
  };

  // Render form fields - reusable function for both create and edit modes
  const renderFormFields = () => {
    return (
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        className="detailForm"
      >
        <Row gutter={[0, 0]}>
          {/* ID - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="ID" name="id">
                <Input disabled />
              </Form.Item>
            </Col>
          )}

          {/* Name */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Name" 
              name="name"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input 
                disabled={readOnly}
                autoComplete="off"
                allowClear
              />
            </Form.Item>
          </Col>

          {/* Comment - Full Width */}
          <Col xs={24} lg={12}>
            <Form.Item 
              label="Comment" 
              name="comment" 
            >
              <TextArea rows={3} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={24}></Col>
          {/* Created By - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="Created By" name="createdby_name">
                <span>{companyData?.createdby_name}</span>
              </Form.Item>
            </Col>
          )}

          {/* Created Date - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="Created Date" name="created_date">
                <span>{companyData?.created_date ? moment(companyData.created_date).format("YYYY-MM-DD HH:mm:ss") : ""}</span>
              </Form.Item>
            </Col>
          )}

          {/* Modified By - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="Modified By" name="lastModified_name">
                <span>{companyData?.lastModified_name}</span>
              </Form.Item>
            </Col>
          )}

          {/* Modified Date - Hidden in create mode */}
          {!isCreatePage && (
            <Col xs={24} lg={12}>
              <Form.Item label="Modified Date" name="modified_date">
                <span>{companyData?.modified_date ? moment(companyData.modified_date).format("YYYY-MM-DD HH:mm:ss") : ""}</span>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    );
  };

  if (loading && !companyData && !isCreatePage) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }
    
  return (
    <div className="company-detail-container">
      <Spin spinning={loading}>
        <DetailTabs
          defaultActiveKey="1" 
          destroyInactiveTabPane={true}
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
        >
          <TabPane
            tab={
              <span>
                <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
              </span>
            }
            key="1"
          >
            <div>
              <div className="slds-m-around_x-small">
                <div className="px-4">
                  <div className="w-full">
                    {!isCreatePage && (isCompanyAdmin || authRole === 1) && (
                      <Alert
                        type="info"
                        showIcon
                        closable
                        style={{ marginBottom: 16 }}
                        message="Workorder cost accounts"
                        description='Configure custom cost account codes (in addition to built-in Maintenance and Capital) on the "Cost accounts" tab.'
                      />
                    )}
                    {renderFormFields()}
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Related and Log tabs - Hidden in create mode */}
          {!isCreatePage && (
            <>
              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-table-list pr-2"></i>Related
                  </span>
                }
                key="2"
              >
                {FacilityRelated(id)}
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-gear pr-2"></i>Setting
                  </span>
                }
                key="4"
              >
                <CompanySetting companyId={id} />
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-sack-dollar pr-2"></i>Cost accounts
                  </span>
                }
                key="5"
              >
                <div className="px-4">
                  <CompanyCostAccountSettings companyId={id} />
                </div>
              </TabPane>

              <TabPane
                tab={
                  <span>
                    <i className="fa-solid fa-timeline pr-2"></i>Log
                  </span>
                }
                key="3"
              >
                <div className="px-4">
                  <RecordAuditTimeline moduleId={id} module="Company" />
                </div>
              </TabPane>


            </>
          )}
        </DetailTabs>
      </Spin>
    </div>
  );
};

export default CompanyDetail;

